import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { COUNTRIES, REFERRAL_SOURCES, SECTORS, TEAM_SIZES } from "../data/formOptions";

export default function OrgForm({ refCode = "", referrer = null }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    organization_name: "",
    sector: "Education",
    team_size: "1–5",
    organization_email: "",
    organization_phone: "",
    country: "United States",
    city: "",
    province_state: "",
    postal_code: "",
    contact_person_name: "",
    contact_person_email: "",
    referral_source: refCode && referrer ? "Partner Referral" : "Google Search",
    accept_terms: false,
    newsletter_opt_in: true,
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target?.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.accept_terms) {
      setError("You must accept the Terms of Service & Privacy Policy.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/register-org", form);
      navigate("/verify", {
        state: {
          kind: "org",
          session_id: data.session_id,
          organization_email: data.organization_email,
          contact_person_email: data.contact_person_email,
          dev_org_otp: data.dev_org_otp,
          dev_contact_otp: data.dev_contact_otp,
        },
      });
    } catch (e2) {
      setError(formatApiError(e2));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="csc-card p-6 lg:p-8 space-y-5" data-testid="org-register-form">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-[#00aaff]">01 / Non-Profit</div>
          <h2 className="mt-1.5 text-2xl lg:text-3xl font-semibold text-white">Register Your Charity</h2>
          <p className="text-sm text-[#94a3b8] mt-1">Get cybersecurity tooling, training &amp; a trusted community.</p>
        </div>
        <div
          className="hidden md:block h-10 w-10 rounded-md border border-[rgba(0,170,255,0.18)]"
          style={{ boxShadow: "inset 0 0 12px rgba(0, 170, 255,0.25)" }}
        />
      </div>

      <div>
        <label className="csc-label">Organization Name *</label>
        <input data-testid="org-name" required className="csc-input" value={form.organization_name} onChange={update("organization_name")} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="csc-label">Organization Sector *</label>
          <select className="csc-input" value={form.sector} onChange={update("sector")} data-testid="org-sector">
            {SECTORS.map((s) => (<option key={s} value={s}>{s}</option>))}
          </select>
        </div>
        <div>
          <label className="csc-label">Team Size *</label>
          <select className="csc-input" value={form.team_size} onChange={update("team_size")} data-testid="org-team-size">
            {TEAM_SIZES.map((t) => (<option key={t} value={t}>{t}</option>))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="csc-label">Organization Email *</label>
          <input data-testid="org-email" required type="email" className="csc-input" value={form.organization_email} onChange={update("organization_email")} />
        </div>
        <div>
          <label className="csc-label">Organization Phone</label>
          <input data-testid="org-phone" className="csc-input" value={form.organization_phone} onChange={update("organization_phone")} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="csc-label">Country *</label>
          <select className="csc-input" value={form.country} onChange={update("country")} data-testid="org-country">
            {COUNTRIES.map((c) => (<option key={c} value={c}>{c}</option>))}
          </select>
        </div>
        <div>
          <label className="csc-label">City / Town</label>
          <input className="csc-input" value={form.city} onChange={update("city")} data-testid="org-city" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="csc-label">Province / State</label>
          <input className="csc-input" value={form.province_state} onChange={update("province_state")} data-testid="org-state" />
        </div>
        <div>
          <label className="csc-label">Postal Code</label>
          <input className="csc-input" value={form.postal_code} onChange={update("postal_code")} data-testid="org-postal" />
        </div>
      </div>

      <div className="pt-3 border-t border-[rgba(0,170,255,0.18)]">
        <div className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#94a3b8] mb-3">
          Primary Contact Person
        </div>
        <div className="grid grid-cols-1 gap-3">
          <div>
            <label className="csc-label">Full Name *</label>
            <input required className="csc-input" value={form.contact_person_name} onChange={update("contact_person_name")} data-testid="org-contact-name" />
          </div>
          <div>
            <label className="csc-label">Contact Email *</label>
            <input required type="email" className="csc-input" value={form.contact_person_email} onChange={update("contact_person_email")} data-testid="org-contact-email" />
          </div>
        </div>
        <p className="text-[11px] text-[#94a3b8] mt-2 font-mono-tech">
          You'll set a password during verification. This contact gets an{" "}
          <span className="text-[#39ff14]">Org Admin</span> login.
        </p>
      </div>

      <div>
        <label className="csc-label">Referral Source</label>
        <select className="csc-input" value={form.referral_source} onChange={update("referral_source")} data-testid="org-referral">
          {REFERRAL_SOURCES.map((r) => (<option key={r} value={r}>{r}</option>))}
        </select>
      </div>

      <div className="space-y-2 pt-1">
        <label className="flex items-start gap-2.5 text-sm text-[#94a3b8] cursor-pointer">
          <input type="checkbox" checked={form.accept_terms} onChange={update("accept_terms")} className="mt-1 accent-[#00aaff]" required data-testid="org-accept-terms" />
          <span>
            I agree to the <a href="/terms" className="csc-link">Terms of Service</a> and{" "}
            <a href="/privacy" className="csc-link">Privacy Policy</a>.
          </span>
        </label>
        <label className="flex items-start gap-2.5 text-sm text-[#94a3b8] cursor-pointer">
          <input type="checkbox" checked={form.newsletter_opt_in} onChange={update("newsletter_opt_in")} className="mt-1 accent-[#39ff14]" data-testid="org-newsletter" />
          <span>Subscribe to our nonprofit cybersecurity briefings.</span>
        </label>
      </div>

      {error && (
        <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/10 rounded px-3 py-2" data-testid="org-form-error">
          {error}
        </div>
      )}

      <button type="submit" disabled={submitting} className="csc-btn-primary w-full" data-testid="org-submit">
        {submitting ? "Sending OTP Codes…" : "Register Organization & Send OTPs"}
      </button>
    </form>
  );
}
